import React, { useEffect, useRef, useState } from 'react'

import update from 'react-addons-update'
import { ErrorBoundary } from 'react-error-boundary'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { ResponsivePXValue } from '@client/components'
import { useConfig } from '@client/contexts/ConfigProvider'
import useIsomorphicLayoutEffect from '@client/hooks/UseIsomorphicLayoutEffect'
import { useGetAppQuery } from '@hooks/api'

const Container = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  justify-content: center;
  ${ResponsivePXValue('height', { mobile: '32px', tablet: '32px', desktop: '40px' })}
  .google-button {
    width: 100%;
  }

`

export interface GoogleIdentityServicesButtonProps {
  disabled: boolean
  action?: 'SIGN UP' | 'LOGIN'
  message: 'use' | 'signin' | 'signup'
  className?: string
  didAuthenticateWithGoogle: (token: string) => void

}

interface GoogleIdentityServicesButtonState {
  disabled: boolean
  loading: boolean
  width: number
  hasInitialized: boolean
}

const DEFAULT_STATE: GoogleIdentityServicesButtonState = {
  disabled: false,
  loading: false,
  width: 0,
  hasInitialized: false,
}

export function GoogleIdentitySocialButton({ message, didAuthenticateWithGoogle }: GoogleIdentityServicesButtonProps): JSX.Element {
  const config = useConfig()
  const { addToast } = useToasts()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const isNativeApplication = appData?.app?.isNativeApplication ?? false

  const [state, setState] = useState<GoogleIdentityServicesButtonState>({ ...DEFAULT_STATE })

  const signInDivRef = useRef(null)
  const buttonRef: React.RefObject<HTMLDivElement> = useRef(null)

  const _handleResponse = (response: { credential: string }): void => {
    try {
      const jwtToken = response.credential
      didAuthenticateWithGoogle(jwtToken)
    } catch (_error) {
      _showError()
    }
  }
  // global google object

  useIsomorphicLayoutEffect(() => {
    const buttonWidth = buttonRef?.current?.clientWidth ?? 0

    setState((prevState) => update(prevState, {
      width: { $set: buttonWidth },
    }))
    // TODO: update on toast header
  }, [])

  useEffect(() => {
    // do not attempt to init on Capacitor app
    if (isNativeApplication) {
      console.warn('Do not use Google Identity Services button within a Capacitor app, use the GoogleSocialButton component instead.')
      return
    }

    if (!state.hasInitialized) {
      try {
        /* global google */

        google.accounts.id.initialize({
          client_id: `${config.getGoogle().clientId}.apps.googleusercontent.com`,
          callback: _handleResponse,
          context: message,
        })

        setState((prevState) => update(prevState, {
          hasInitialized: { $set: true },
        }))
      } catch (_error) {
        setState((prevState) => update(prevState, {
          hasInitialized: { $set: false },
        }))
        _showError()
      }
    }

    if (state.hasInitialized && state.width !== 0) {
      try {
        /* global google */

        google.accounts.id.renderButton(signInDivRef.current, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          width: state.width,
        })
      } catch (_error) {
        _showError()
      }
    }
  }, [signInDivRef.current, state.hasInitialized])

  const _showError = (): void => {
    addToast('Google was unable to authenticate you.', {
      appearance: 'error',
      autoDismiss: true,
    })
  }

  return (
    <Container ref={buttonRef}>
      <ErrorBoundary fallback={<div>Google signin service temporarily un-available, please contact customer service for support....</div>}>
        <div id='signinDiv' className='google-button' ref={signInDivRef}></div>
      </ErrorBoundary>
    </Container>
  )
}
